import React, { useState, useRef, useEffect } from 'react';
import {
  Grid, Box, Paper, Typography, List, ListItem, ListItemText, Avatar, useMediaQuery, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Menu, MenuItem, Fab, Badge, IconButton, InputAdornment, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import ForwardIcon from '@mui/icons-material/Forward';
import SendIcon from '@mui/icons-material/Send';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
// import api utility
import api from '../api';

// Mock chat history with more messages for testing scroll
const chatHistoryMock = {
  1: [
    { id: 1, type: 'message', sender: 'support', name: 'Support Team', message: 'Hello! How can I help you today?', timestamp: '10:00 AM' },
    { id: 2, type: 'message', sender: 'user', name: 'You', message: 'Hi, I missed a call earlier.', timestamp: '10:01 AM' },
    { id: 3, type: 'message', sender: 'support', name: 'Support Team', message: 'I can help you with that. Let me check your recent call history.', timestamp: '10:02 AM' },
    { id: 4, type: 'missed_call', timestamp: '09:30 AM', comment: 'Customer called about billing inquiry' },
    { id: 5, type: 'message', sender: 'support', name: 'Support Team', message: 'I can see you had a missed call at 9:30 AM. Would you like me to help you with your billing inquiry?', timestamp: '10:03 AM' },
    { id: 6, type: 'message', sender: 'user', name: 'You', message: 'Yes, please. I had some questions about my recent invoice.', timestamp: '10:04 AM' },
    { id: 7, type: 'message', sender: 'support', name: 'Support Team', message: 'Of course! I\'d be happy to help clarify any questions about your invoice. Could you please provide me with your account number or the invoice number you\'re asking about?', timestamp: '10:05 AM' },
    { id: 8, type: 'message', sender: 'user', name: 'You', message: 'My account number is ACC-123456. The invoice I\'m asking about is from last month.', timestamp: '10:06 AM' },
    { id: 9, type: 'message', sender: 'support', name: 'Support Team', message: 'Perfect! Let me pull up your account details. I can see your account and the invoice from last month. What specific questions do you have about it?', timestamp: '10:07 AM' },
    { id: 10, type: 'message', sender: 'user', name: 'You', message: 'I noticed there was an extra charge that I don\'t recognize. Can you help me understand what it\'s for?', timestamp: '10:08 AM' },
  ],
  2: [
    { id: 1, type: 'missed_call', timestamp: '09:00 AM', comment: 'Follow-up call needed' },
    { id: 2, type: 'message', sender: 'user', name: 'You', message: 'Can you call me back?', timestamp: '09:05 AM' },
    { id: 3, type: 'message', sender: 'support', name: 'Support Team', message: 'I\'ll call you back within the next hour.', timestamp: '09:10 AM' },
  ],
  3: [
    { id: 1, type: 'missed_call', timestamp: '08:00 AM', comment: 'Technical support needed' },
    { id: 2, type: 'message', sender: 'support', name: 'Support Team', message: 'We missed your call. How can we help?', timestamp: '08:10 AM' },
    { id: 3, type: 'message', sender: 'user', name: 'You', message: 'I\'m having trouble with the app.', timestamp: '08:15 AM' },
  ],
};

function Interactions() {
  const isMdDown = useMediaQuery(theme => theme.breakpoints.down('md'));
  const [activeChats, setActiveChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [chatHistory, setChatHistory] = useState(chatHistoryMock[selectedContact?.id] || []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const [commentMissedCallIdx, setCommentMissedCallIdx] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [msgMenuAnchorEl, setMsgMenuAnchorEl] = useState(null);
  const [msgMenuIdx, setMsgMenuIdx] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignUserValue, setAssignUserValue] = useState('');
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  
  // Tooltip states
  const [tooltipOpen, setTooltipOpen] = useState({ close: false, hold: false, forward: false });
  
  // Last seen visibility state
  const [showLastSeen, setShowLastSeen] = useState(false);
  
  // New states for scroll management
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNearBottom, setIsNearBottom] = useState(true);
  
  // Separate states for User Chat area
  const [showUserChatScrollToBottom, setShowUserChatScrollToBottom] = useState(false);
  const [userChatUnreadCount, setUserChatUnreadCount] = useState(0);
  const [isUserChatNearBottom, setIsUserChatNearBottom] = useState(true);
  
  // Separate chat history for User Chat area
  const [userChatHistory, setUserChatHistory] = useState([
    // Sample messages for testing scroll
    {
      id: 1,
      type: 'message',
      sender: 'support',
      name: 'Support Team',
      message: 'Hello! How can I help you today?',
      timestamp: '10:00 AM'
    },
    {
      id: 2,
      type: 'message',
      sender: 'user',
      name: 'You',
      message: 'Hi, I need help with my account.',
      timestamp: '10:01 AM'
    },
    {
      id: 3,
      type: 'message',
      sender: 'support',
      name: 'Support Team',
      message: 'Of course! I\'d be happy to help you with your account. Could you please provide me with your account number or username?',
      timestamp: '10:02 AM'
    },
    {
      id: 4,
      type: 'message',
      sender: 'user',
      name: 'You',
      message: 'My account number is ACC-123456.',
      timestamp: '10:03 AM'
    },
    {
      id: 5,
      type: 'message',
      sender: 'support',
      name: 'Support Team',
      message: 'Perfect! Let me pull up your account details. I can see your account and the recent activity. What specific issue are you experiencing?',
      timestamp: '10:04 AM'
    },
    {
      id: 6,
      type: 'message',
      sender: 'user',
      name: 'You',
      message: 'I noticed there was an extra charge that I don\'t recognize. Can you help me understand what it\'s for?',
      timestamp: '10:05 AM'
    },
    {
      id: 7,
      type: 'message',
      sender: 'support',
      name: 'Support Team',
      message: 'I can help you with that. Let me check your recent billing history and identify that charge for you.',
      timestamp: '10:06 AM'
    },
    {
      id: 8,
      type: 'message',
      sender: 'user',
      name: 'You',
      message: 'Thank you for your help!',
      timestamp: '10:07 AM'
    },
    {
      id: 9,
      type: 'message',
      sender: 'support',
      name: 'Support Team',
      message: 'You\'re welcome! I\'m here to help. Is there anything else you need assistance with today?',
      timestamp: '10:08 AM'
    },
    {
      id: 10,
      type: 'message',
      sender: 'user',
      name: 'You',
      message: 'That should be all for now. Thanks again!',
      timestamp: '10:09 AM'
    },
    // Additional messages to ensure scrolling is needed
    {
      id: 11,
      type: 'message',
      sender: 'support',
      name: 'Support Team',
      message: 'Great! I\'m glad I could help you today. Your account is now up to date and all charges have been explained.',
      timestamp: '10:10 AM'
    },
    {
      id: 12,
      type: 'message',
      sender: 'user',
      name: 'You',
      message: 'Perfect! Everything is clear now.',
      timestamp: '10:11 AM'
    },
    {
      id: 13,
      type: 'message',
      sender: 'support',
      name: 'Support Team',
      message: 'Excellent! If you have any more questions in the future, don\'t hesitate to reach out. We\'re always here to help.',
      timestamp: '10:12 AM'
    },
    {
      id: 14,
      type: 'message',
      sender: 'user',
      name: 'You',
      message: 'Will do! Thanks for the excellent service.',
      timestamp: '10:13 AM'
    },
    {
      id: 15,
      type: 'message',
      sender: 'support',
      name: 'Support Team',
      message: 'You\'re very welcome! Have a wonderful day ahead.',
      timestamp: '10:14 AM'
    },
    {
      id: 16,
      type: 'message',
      sender: 'user',
      name: 'You',
      message: 'You too! Goodbye!',
      timestamp: '10:15 AM'
    },
    {
      id: 17,
      type: 'message',
      sender: 'support',
      name: 'Support Team',
      message: 'Goodbye! Take care!',
      timestamp: '10:16 AM'
    }
  ]);
  
  const chatAreaRef = useRef(null);
  const userChatAreaRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);

  // Fetch patients as active chats
  useEffect(() => {
    const fetchPatients = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/patients');
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          const chats = res.data.data.map((item) => ({
            id: item._id,
            name: item.TA_Patient_Name || '-',
            lastMessage: item.TA_Patient_Email_Address || '-', // Or any other field for last message
            time: item.TA_Created_At ? new Date(item.TA_Created_At).toLocaleDateString() : '-',
            avatar: (item.TA_Patient_Name && item.TA_Patient_Name[0]) || 'P',
            phone: item.TA_Patient_Mobile_Number || '-',
            email: item.TA_Patient_Email_Address || '-',
          }));
          setActiveChats(chats);
          setSelectedContact(chats[0] || null);
        } else {
          setActiveChats([]);
          setSelectedContact(null);
        }
      } catch (err) {
        setError('Failed to fetch patients');
        setActiveChats([]);
        setSelectedContact(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPatients();
  }, []);

  // Update chat history when selectedContact changes
  useEffect(() => {
    if (!selectedContact?.id) {
      setChatHistory([]);
      return;
    }
    setChatLoading(true);
    setChatError(null);
    const fetchMessages = async () => {
      try {
        const res = await api.get(`/patients/${selectedContact.id}/messages`);
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          // Map messages to chat format
          const mapped = res.data.data.map((msg, idx) => ({
            id: idx + 1,
            type: 'message',
            sender: 'user', // or 'support' if you can distinguish
            name: 'You', // or use a real name if available
            message: typeof msg === 'string' ? msg : (msg.messageText || ''),
            timestamp: '' // If your API provides a timestamp, use it here
          }));
          setChatHistory(mapped);
        } else {
          setChatHistory([]);
        }
      } catch (err) {
        setChatError('Failed to load messages');
        setChatHistory([]);
      } finally {
        setChatLoading(false);
      }
    };
    fetchMessages();
    setUnreadCount(0);
    setShowScrollToBottom(false);
    setUserChatUnreadCount(0);
    setShowUserChatScrollToBottom(false);
  }, [selectedContact]);

  // Ensure User Chat area scrolls to bottom when new messages are added
  useEffect(() => {
    if (userChatAreaRef.current && isUserChatNearBottom) {
      userChatAreaRef.current.scrollTop = userChatAreaRef.current.scrollHeight;
    }
  }, [userChatHistory, isUserChatNearBottom]);

  // Debug scroll dimensions
  useEffect(() => {
    if (userChatAreaRef.current) {
      const { scrollHeight, clientHeight } = userChatAreaRef.current;
      console.log('User Chat Scroll Debug:', { scrollHeight, clientHeight, needsScroll: scrollHeight > clientHeight });
    }
  }, [userChatHistory]);

  // Auto-scroll to bottom when chatHistory changes (only if user is near bottom)
  useEffect(() => {
    if (chatAreaRef.current && isNearBottom) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    } else if (chatAreaRef.current && !isNearBottom) {
      // If user is not near bottom, increment unread count for new messages
      setUnreadCount(prev => prev + 1);
    }
  }, [chatHistory, isNearBottom]);

  // Handle scroll events to determine if user is near bottom
  const handleScroll = () => {
    if (chatAreaRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold
      const shouldShowButton = scrollHeight > clientHeight && !isAtBottom;
      
      setIsNearBottom(isAtBottom);
      setShowScrollToBottom(shouldShowButton);
      
      if (isAtBottom) {
        setUnreadCount(0);
      }
    }
  };

  // Handle scroll events for User Chat area
  const handleUserChatScroll = () => {
    if (userChatAreaRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = userChatAreaRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold
      const shouldShowButton = scrollHeight > clientHeight && !isAtBottom;
      
      setIsUserChatNearBottom(isAtBottom);
      setShowUserChatScrollToBottom(shouldShowButton);
      
      if (isAtBottom) {
        setUserChatUnreadCount(0);
      }
      
      console.log('User Chat Scroll:', { scrollTop, scrollHeight, clientHeight, isAtBottom, shouldShowButton });
    }
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTo({
        top: chatAreaRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setUnreadCount(0);
      setShowScrollToBottom(false);
    }
  };

  // Scroll to bottom function for User Chat area
  const scrollUserChatToBottom = () => {
    if (userChatAreaRef.current) {
      userChatAreaRef.current.scrollTo({
        top: userChatAreaRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setUserChatUnreadCount(0);
      setShowUserChatScrollToBottom(false);
    }
  };

  // Handle right-click on missed call
  const handleMissedCallContextMenu = (event, idx) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setCommentMissedCallIdx(idx);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddComment = () => {
    setCommentDialogOpen(true);
    setAnchorEl(null);
    setCommentValue(chatHistory[commentMissedCallIdx]?.comment || '');
  };

  const handleCommentSave = () => {
    const updated = chatHistory.map((item, idx) =>
      idx === commentMissedCallIdx ? { ...item, comment: commentValue } : item
    );
    setChatHistory(updated);
    chatHistoryMock[selectedContact?.id] = updated;
    setCommentDialogOpen(false);
    setCommentValue('');
  };

  const handleSend = async () => {
    if (!inputMessage.trim() || !selectedContact?.id) return;
    try {
      const payload = {
        patientId: selectedContact.id,
        messageText: inputMessage,
        templateId: '' // You can set a templateId if needed
      };
      const res = await api.post('/patients/send-message', payload);
      if (res.data && res.data.success) {
    const newMsg = {
      id: chatHistory.length + 1,
      type: 'message',
      sender: 'user',
      name: 'You',
      message: inputMessage,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    const updated = [...chatHistory, newMsg];
    setChatHistory(updated);
        chatHistoryMock[selectedContact?.id] = updated;
        setSnackbar({ open: true, message: 'Message sent successfully', severity: 'success' });
    setInputMessage('');
      } else {
        setSnackbar({ open: true, message: 'Failed to send message', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to send message', severity: 'error' });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle right-click on message bubble
  const handleMsgContextMenu = (event, idx) => {
    event.preventDefault();
    setMsgMenuAnchorEl(event.currentTarget);
    setMsgMenuIdx(idx);
  };
  
  const handleMsgMenuClose = () => {
    setMsgMenuAnchorEl(null);
    setMsgMenuIdx(null);
  };
  
  const handleAssignUser = () => {
    setAssignDialogOpen(true);
    setMsgMenuAnchorEl(null);
    setAssignUserValue('');
  };
  
  const handleAssignUserSave = () => {
    setAssignDialogOpen(false);
    setAssignUserValue('');
  };

  // Forward message functionality
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [forwardToValue, setForwardToValue] = useState('');
  const [forwardMessageValue, setForwardMessageValue] = useState('');
  const [pendingForwardedMessage, setPendingForwardedMessage] = useState(null);

  const handleForwardMessage = () => {
    setForwardDialogOpen(true);
    setMsgMenuAnchorEl(null);
    // Get the selected message content
    const selectedMessage = chatHistory[msgMenuIdx];
    setForwardMessageValue(selectedMessage?.message || '');
    setForwardToValue('');
  };

  const handleForwardMessageSave = () => {
    // Create a pending forwarded message with Sent/Cancel buttons
    const pendingMessage = {
      id: Date.now(),
      type: 'pending_forward',
      sender: 'user',
      name: 'You',
      message: forwardMessageValue,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isPending: true
    };
    
    // Add to User Chat history as pending message
    const updatedUserChat = [...userChatHistory, pendingMessage];
    setUserChatHistory(updatedUserChat);
    setPendingForwardedMessage(pendingMessage);
    
    console.log('Forwarding message:', forwardMessageValue);
    setForwardDialogOpen(false);
    setForwardToValue('');
    setForwardMessageValue('');
  };

  const handleForwardSent = () => {
    if (pendingForwardedMessage) {
      // Convert pending message to regular message
      const updatedUserChat = userChatHistory.map(msg => 
        msg.id === pendingForwardedMessage.id 
          ? { ...msg, isPending: false, type: 'message' }
          : msg
      );
      setUserChatHistory(updatedUserChat);
      setPendingForwardedMessage(null);
    }
  };

  const handleForwardCancel = () => {
    if (pendingForwardedMessage) {
      // Remove the pending message
      const updatedUserChat = userChatHistory.filter(msg => msg.id !== pendingForwardedMessage.id);
      setUserChatHistory(updatedUserChat);
      setPendingForwardedMessage(null);
    }
  };

  // Filter active chats based on search query
  const filteredActiveChats = activeChats.filter(chat =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (chat.lastMessage || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>


      {/* Main Content Area */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        minHeight: 0, 
        overflow: 'hidden',
        p: { xs: 1, sm: 1.5, md: 2, lg: 3 },
        background: 'transparent'
      }}>
      <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ 
        flex: 1, 
        minHeight: 0, 
        overflow: 'hidden',
        height: '100%'
      }}>
        {/* Left Panel: Active Chats */}
        <Grid item xs={12} lg={3} md={4} sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: 0, 
          height: '100%',
          order: { xs: 1, lg: 1 }
        }}>
          <Paper sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: 0, 
            height: '100%',
            maxHeight: '100%',
            background: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e2e8f0',
          }}>
            <Box sx={{ 
              p: 2.5, 
              background: 'linear-gradient(135deg, #166534 0%, #14532d 100%)',
              borderRadius: '16px 16px 0 0',
              display: 'flex', 
              flexDirection: 'column',
              flexShrink: 0,
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 2,
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  color: 'white',
                  fontSize: '1.1rem',
                }}>
                  Active Chats
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onMouseEnter={() => setIsSearchExpanded(true)}
                  onMouseLeave={() => {
                    if (!searchQuery) {
                      setIsSearchExpanded(false);
                    }
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search chats..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                      width: isSearchExpanded ? 200 : 0,
                      transition: 'width 0.3s ease',
                      overflow: 'hidden',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '20px',
                        height: 36,
                        fontSize: 14,
                        background: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 1)',
                        },
                        '&.Mui-focused': {
                          background: 'rgba(255, 255, 255, 1)',
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.8)' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {!isSearchExpanded && (
                    <IconButton
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.3)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
              
              {/* Filter Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 0.5, sm: 1 },
                justifyContent: 'center',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    background: '#166534',
                    color: 'white',
                    borderRadius: '20px',
                    px: { xs: 1.5, sm: 2 },
                    py: 0.5,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    border: '1px solid #14532d',
                    minWidth: { xs: 'auto', sm: 'unset' },
                    '&:hover': {
                      background: '#14532d',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(20,83,45,0.4)',
                    },
                    '&.Mui-selected': {
                      background: '#14532d',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Open
                    <Box sx={{
                      background: '#166534',
                      color: 'white',
                      borderRadius: '50%',
                      width: { xs: 18, sm: 20 },
                      height: { xs: 18, sm: 20 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      fontWeight: 700,
                      border: '1px solid #14532d',
                    }}>
                      36
                    </Box>
                  </Box>
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    background: '#166534',
                    color: 'white',
                    borderRadius: '20px',
                    px: { xs: 1.5, sm: 2 },
                    py: 0.5,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    border: '1px solid #14532d',
                    minWidth: { xs: 'auto', sm: 'unset' },
                    '&:hover': {
                      background: '#14532d',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(20,83,45,0.4)',
                    },
                    '&.Mui-selected': {
                      background: '#14532d',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Closed
                    <Box sx={{
                      background: '#166534',
                      color: 'white',
                      borderRadius: '50%',
                      width: { xs: 18, sm: 20 },
                      height: { xs: 18, sm: 20 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      fontWeight: 700,
                      border: '1px solid #14532d',
                    }}>
                      29
                    </Box>
                  </Box>
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    background: '#166534',
                    color: 'white',
                    borderRadius: '20px',
                    px: { xs: 1.5, sm: 2 },
                    py: 0.5,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    border: '1px solid #14532d',
                    minWidth: { xs: 'auto', sm: 'unset' },
                    '&:hover': {
                      background: '#14532d',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(20,83,45,0.4)',
                    },
                    '&.Mui-selected': {
                      background: '#14532d',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Pro
                    <Box sx={{
                      background: '#166534',
                      color: 'white',
                      borderRadius: '50%',
                      width: { xs: 18, sm: 20 },
                      height: { xs: 18, sm: 20 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      fontWeight: 700,
                      border: '1px solid #14532d',
                    }}>
                      12
                    </Box>
                  </Box>
                </Button>
              </Box>
            </Box>
            <Box sx={{ 
              flex: 1, 
              minHeight: 0, 
              height: '100%',
              overflow: 'auto', 
              p: 1,
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': { 
                width: '8px' 
              },
              '&::-webkit-scrollbar-track': { 
                background: 'rgba(0, 0, 0, 0.05)',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb': { 
                background: 'rgba(20,83,45,0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(20,83,45,0.5)'
                }
              },
              '&::-webkit-scrollbar-corner': {
                background: 'transparent'
              }
            }}>
              <List sx={{ minHeight: 0, p: 0 }}>
                {loading ? (
                  <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.9rem' }}>Loading patients...</Typography>
                  </Box>
                ) : error ? (
                  <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
                    <Typography variant="body2" color="error">{error}</Typography>
                  </Box>
                ) : filteredActiveChats.length > 0 ? (
                  filteredActiveChats.map(contact => (
                    <ListItem 
                      key={contact.id} 
                      button 
                      selected={selectedContact?.id === contact.id} 
                      onClick={() => setSelectedContact(contact)}
                      sx={{
                        borderRadius: '12px',
                        mb: 0.5,
                        mx: 0.5,
                        p: 1.5,
                        transition: 'all 0.2s ease',
                        background: selectedContact?.id === contact.id 
                          ? 'rgba(20,83,45,0.1)'
                          : 'transparent',
                        '&:hover': {
                          background: 'rgba(20,83,45,0.05)',
                        },
                        '&.Mui-selected': {
                          background: 'rgba(20,83,45,0.15)',
                          border: '1px solid rgba(20,83,45,0.2)',
                        },
                      }}
                    >
                      <Avatar sx={{ 
                        mr: 2,
                        width: 40,
                        height: 40,
                        background: 'linear-gradient(135deg, #166534 0%, #14532d 100%)',
                        fontSize: '1rem',
                        fontWeight: 600,
                      }}>
                        {contact.avatar}
                      </Avatar>
                      <ListItemText 
                        primary={
                          <Typography sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.95rem',
                            color: selectedContact?.id === contact.id ? '#166534' : '#1e293b',
                          }}>
                            {contact.name}
                          </Typography>
                        } 
                        secondary={
                          <Typography sx={{ 
                            fontSize: '0.8rem',
                            color: '#64748b',
                            lineHeight: 1.3,
                          }}>
                            {contact.lastMessage}
                          </Typography>
                        } 
                      />
                      <Typography variant="caption" sx={{ 
                        color: '#64748b',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        background: '#f1f5f9',
                        px: 1,
                        py: 0.3,
                        borderRadius: '8px',
                      }}>
                        {contact.time}
                      </Typography>
                  </ListItem>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
                    <Typography variant="body2" sx={{ 
                      color: '#64748b',
                      fontSize: '0.9rem',
                    }}>
                      {searchQuery ? 'No chats found' : 'No active chats'}
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          </Paper>
        </Grid>
        
        {/* Center Panel: Chat Interface */}
        <Grid item xs={12} lg={6} md={5} sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: 0, 
          height: '100%',
          order: { xs: 2, lg: 2 }
        }}>
          <Paper sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: 0, 
            p: 0, 
            position: 'relative', 
            height: '100%',
            maxHeight: '100%',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <Box sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider', 
              background: 'linear-gradient(135deg, #166534 0%, #14532d 100%)',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              boxShadow: '0 2px 20px rgba(0,0,0,0.1)'
            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ 
                    mr: 2, 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: '2px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    {selectedContact?.avatar}
                  </Avatar>
                  <Box
                    onMouseEnter={() => setShowLastSeen(true)}
                    onMouseLeave={() => setShowLastSeen(false)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'white', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                      {selectedContact?.name}
                    </Typography>
                    {showLastSeen && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.75rem',
                          display: 'block',
                          color: 'rgba(255,255,255,0.8)',
                          animation: 'fadeIn 0.3s ease-in-out',
                          '@keyframes fadeIn': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(-5px)'
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateY(0)'
                            }
                          }
                        }}
                      >
                        last seen today at 2:30 PM
                      </Typography>
                    )}
                  </Box>
                </Box>
              
              {/* Action Buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  size="small"
                  sx={{
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    color: 'white',
                    width: 28,
                    height: 28,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)',
                    '&:hover': {
                      bgcolor: 'error.dark',
                      transform: 'scale(1.1) rotate(90deg)',
                      boxShadow: '0 4px 16px rgba(211, 47, 47, 0.5)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                  onClick={() => console.log('Close ticket clicked')}
                  title="Close Ticket"
                >
                  <CloseIcon sx={{ 
                    fontSize: 12,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'rotate(180deg)',
                    }
                  }} />
                </IconButton>
                
                <IconButton
                  size="small"
                  sx={{
                    borderRadius: '50%',
                    bgcolor: 'warning.main',
                    color: 'white',
                    width: 28,
                    height: 28,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                    '&:hover': {
                      bgcolor: 'warning.dark',
                      transform: 'scale(1.1)',
                      boxShadow: '0 4px 16px rgba(255, 152, 0, 0.5)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                  onClick={() => console.log('Hold chat clicked')}
                  title="Hold Chat"
                >
                  <PauseIcon sx={{ 
                    fontSize: 12,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.2)',
                    }
                  }} />
                </IconButton>
                
                <IconButton
                  size="small"
                  sx={{
                    borderRadius: '50%',
                    bgcolor: 'info.main',
                    color: 'white',
                    width: 28,
                    height: 28,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                    '&:hover': {
                      bgcolor: 'info.dark',
                      transform: 'scale(1.1) translateX(2px)',
                      boxShadow: '0 4px 16px rgba(33, 150, 243, 0.5)',
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                    },
                  }}
                  onClick={() => console.log('Forward chat clicked')}
                  title="Forward Chat"
                >
                  <ForwardIcon sx={{ 
                    fontSize: 12,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(1px)',
                    }
                  }} />
                </IconButton>
              </Box>
            </Box>
            
            {/* Scrollable chat area */}
            <Box
              ref={chatAreaRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: 'calc(100% - 65px)',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 2,
                pb: 3,
                position: 'relative',
                background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
                // Custom scrollbar styles
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '10px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                  }
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              {chatLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
                  <Typography variant="body1">Loading messages...</Typography>
                </Box>
              ) : chatError ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
                  <Typography variant="body1" color="error">{chatError}</Typography>
                </Box>
              ) : chatHistory.map((item, idx) => {
                if (item.type === 'missed_call') {
                  return (
                    <Box key={idx} sx={{ 
                      mb: 2, 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'flex-start', 
                      gap: 1,
                      animation: 'fadeInUp 0.4s ease-out',
                      '@keyframes fadeInUp': {
                        '0%': {
                          opacity: 0,
                          transform: 'translateY(20px)'
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'translateY(0)'
                        }
                      }
                    }}>
                      <Paper
                        sx={{ 
                          p: 1.5, 
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                          borderRadius: 4, 
                          minWidth: 180, 
                          maxWidth: 200, 
                          boxShadow: '0 8px 25px rgba(255, 107, 107, 0.3)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 12px 35px rgba(255, 107, 107, 0.4)',
                          }
                        }}
                        onContextMenu={e => handleMissedCallContextMenu(e, idx)}
                      >
                        <Typography sx={{ 
                          color: 'white', 
                          fontWeight: 700,
                          textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                          fontSize: '0.9rem'
                        }}>
                          Missed Call
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255,255,255,0.9)',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}>
                          {item.timestamp}
                        </Typography>
                      </Paper>
                      {item.comment && (
                        <Accordion
                          expanded={expandedAccordion === idx}
                          onChange={() => setExpandedAccordion(expandedAccordion === idx ? false : idx)}
                          sx={{
                            minWidth: 80,
                            maxWidth: 160,
                            boxShadow: 1,
                            borderRadius: 2,
                            bgcolor: '#fff',
                            ml: 0.5,
                            '& .MuiAccordionSummary-root': {
                              minHeight: 32,
                              px: 1.5,
                              py: 0.5,
                            },
                            '& .MuiAccordionDetails-root': {
                              px: 1.5,
                              py: 1,
                            },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
                            <Typography fontWeight={600} fontSize={14}>Comment</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography fontSize={14}>{item.comment}</Typography>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Box>
                  );
                }
                
                // Custom chat bubble
                const isUser = item.sender === 'user';
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      justifyContent: isUser ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-start',
                      animation: 'slideIn 0.3s ease-out',
                      '@keyframes slideIn': {
                        '0%': {
                          opacity: 0,
                          transform: isUser ? 'translateX(20px)' : 'translateX(-20px)'
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'translateX(0)'
                        }
                      }
                    }}
                  >
                    <Paper
                      sx={{
                        borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                        p: 1.5,
                        minWidth: 120,
                        maxWidth: '75%',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        boxShadow: isUser 
                          ? '0 4px 20px rgba(20,83,45,0.15)' 
                          : '0 4px 20px rgba(0, 0, 0, 0.08)',
                        background: isUser 
                          ? 'linear-gradient(135deg, #166534 0%, #14532d 100%)' 
                          : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        ml: isUser ? 4 : 0,
                        mr: isUser ? 0 : 4,
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        wordBreak: 'break-word',
                        border: isUser ? 'none' : '1px solid rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: isUser 
                            ? '0 8px 30px rgba(20,83,45,0.25)' 
                            : '0 8px 30px rgba(0, 0, 0, 0.12)',
                        }
                      }}
                      onContextMenu={e => handleMsgContextMenu(e, idx)}
                    >
                      <Typography fontWeight={700} sx={{ 
                        mb: 0.3, 
                        color: isUser ? 'white' : '#2d3748',
                        textShadow: isUser ? '0 1px 2px rgba(0,0,0,0.2)' : 'none',
                        fontSize: '0.9rem'
                      }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ 
                        mb: 0.5, 
                        color: isUser ? 'white' : '#2d3748',
                        lineHeight: 1.4,
                        fontSize: '0.9rem',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }}>
                          {item.message}
                        </Typography>
                      <Typography variant="caption" sx={{ 
                        alignSelf: 'flex-end',
                        color: isUser ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.5)',
                        fontSize: '0.65rem',
                        fontWeight: 500
                      }}>
                        {item.timestamp || 'Yesterday'}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })}
            </Box>

            {/* Scroll to bottom button */}
            {showScrollToBottom && (
              <Fab
                size="small"
                color="primary"
                onClick={scrollToBottom}
                sx={{
                  position: 'absolute',
                  bottom: 80,
                  right: 20,
                  zIndex: 1000,
                  boxShadow: 3,
                }}
              >
                <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error">
                  <KeyboardArrowDownIcon />
                </Badge>
              </Fab>
            )}
            
            {/* Input area */}
            <Box sx={{ 
              p: { xs: 1.8, md: 2.2 }, 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.8, md: 1 }, 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              position: 'relative',
              height: { xs: 65, md: 72 },
              flexShrink: 0,
              zIndex: 10,
              minHeight: { xs: 65, md: 72 },
              maxHeight: { xs: 65, md: 72 },
              boxShadow: '0 -8px 32px rgba(0,0,0,0.08)',
              backdropFilter: 'blur(20px)',
              borderTop: '1px solid rgba(0,0,0,0.06)',
              mb: 0
            }}>
              <TextField
                fullWidth
                placeholder="Type a message... Use @ to tag users"
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                multiline
                minRows={1}
                maxRows={3}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.95)', 
                  borderRadius: 8,
                  backdropFilter: 'blur(20px)',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    background: 'rgba(255,255,255,0.9)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: 'rgba(99, 102, 241, 0.3)',
                      boxShadow: '0 6px 30px rgba(0,0,0,0.12)',
                      transform: 'translateY(-2px)',
                      background: 'rgba(255,255,255,0.98)',
                    },
                    '&.Mui-focused': {
                      borderColor: '#6366f1',
                      boxShadow: '0 6px 30px rgba(99, 102, 241, 0.15)',
                      background: 'rgba(255,255,255,1)',
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                    padding: { xs: '8px 12px', md: '10px 14px' },
                    lineHeight: 1.3,
                    color: '#1f2937',
                    fontWeight: 400,
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#9ca3af',
                    opacity: 1,
                    fontWeight: 400,
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1 }}>
                      <IconButton
                        size="small"
                        sx={{
                          color: '#6366f1',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          borderRadius: '50%',
                          width: 24,
                          height: 24,
                          '&:hover': {
                            color: '#4f46e5',
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                            transform: 'scale(1.1) rotate(180deg)',
                            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.2)',
                          }
                        }}
                      >
                        <KeyboardArrowDownIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Box>
                  )
                }}
              />
              <IconButton
                onClick={handleSend}
                sx={{
                  background: 'linear-gradient(135deg, #166534 0%, #14532d 100%)',
                  color: 'white',
                  width: { xs: 42, md: 46 },
                  height: { xs: 42, md: 46 },
                  borderRadius: '50%',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 6px 25px rgba(20,83,45,0.3)',
                  flexShrink: 0,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'scale(1.1) translateY(-3px)',
                    boxShadow: '0 10px 35px rgba(20,83,45,0.4)',
                    '&::before': {
                      opacity: 1,
                    }
                  },
                  '&:active': {
                    transform: 'scale(0.95) translateY(0px)',
                  }
                }}
              >
                <SendIcon sx={{ 
                  fontSize: { xs: 18, md: 20 },
                  position: 'relative',
                  zIndex: 1,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateX(3px)',
                  }
                }} />
              </IconButton>
            </Box>
          </Paper>
        </Grid>
        
        {/* Right Panel: Contact Details */}
        <Grid item xs={12} lg={3} md={3} sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: 0, 
          height: '100%',
          order: { xs: 3, lg: 3 }
        }}>
          {/* First Contact Details Box (now on top) */}
          <Paper sx={{ p: 2, minHeight: 0, flexShrink: 0 }}>
            <Typography variant="h6">Contact Details</Typography>
            {selectedContact ? (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>{selectedContact.name}</Typography>
                {selectedContact.email && (
                  <Typography variant="body2" color="text.secondary">Email: {selectedContact.email}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">Phone: +91 1234567890</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Status: {isNearBottom ? 'Viewing latest messages' : 'Scrolled up'}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">Select a contact to view details.</Typography>
            )}
          </Paper>
          
          {/* Second Contact Details Box (now below) */}
          <Paper sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 0, position: 'relative', height: '500px', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider', 
              background: 'linear-gradient(135deg, #166534 0%, #14532d 100%)',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              boxShadow: '0 4px 25px rgba(224, 242, 254, 0.4)'
            }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: 'white', textShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                User Chat
              </Typography>
            </Box>
            
            {/* Scrollable chat area */}
            <Box
              ref={userChatAreaRef}
              onScroll={handleUserChatScroll}
              sx={{
                flex: 1,
                minHeight: 0,
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 1.5,
                pb: 2,
                position: 'relative',
                background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
                // Enable mouse wheel scrolling
                scrollBehavior: 'smooth',
                // Custom scrollbar styles
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: '3px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.5)',
                  }
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  borderRadius: '3px',
                },
                // Ensure scrolling works
                '&::-webkit-scrollbar-corner': {
                  backgroundColor: 'transparent',
                },
                // Mouse wheel scrolling
                '&:hover': {
                  cursor: 'default',
                },
                // Force scrollbar to always show
                '&::-webkit-scrollbar-thumb:vertical': {
                  minHeight: '20px',
                },
              }}
            >
              {userChatHistory.map((item, idx) => {
                if (item.type === 'missed_call') {
                  return (
                    <Box key={idx} sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'flex-start', 
                      gap: 0.5,
                      animation: 'fadeInUp 0.4s ease-out',
                      '@keyframes fadeInUp': {
                        '0%': {
                          opacity: 0,
                          transform: 'translateY(20px)'
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'translateY(0)'
                        }
                      }
                    }}>
                      <Paper
                        sx={{ 
                          p: 1, 
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                          borderRadius: 3, 
                          minWidth: 150, 
                          maxWidth: 170, 
                          boxShadow: '0 6px 20px rgba(255, 107, 107, 0.3)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(255, 107, 107, 0.4)',
                          }
                        }}
                      >
                        <Typography sx={{ 
                          color: 'white', 
                          fontWeight: 700,
                          textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                          fontSize: '0.9rem'
                        }}>
                          Missed Call
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: 'rgba(255,255,255,0.9)',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}>
                          {item.timestamp}
                        </Typography>
                      </Paper>
                    </Box>
                  );
                }
                
                // Custom chat bubble
                const isUser = item.sender === 'user';
                const isPendingForward = item.type === 'pending_forward';
                
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      justifyContent: isUser ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-start',
                      animation: 'slideIn 0.3s ease-out',
                      '@keyframes slideIn': {
                        '0%': {
                          opacity: 0,
                          transform: isUser ? 'translateX(20px)' : 'translateX(-20px)'
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'translateX(0)'
                        }
                      }
                    }}
                  >
                    <Paper
                      sx={{
                        maxWidth: '65%',
                        p: 1,
                        borderRadius: 2,
                        background: isUser 
                          ? 'linear-gradient(135deg, #166534 0%, #14532d 100%)'
                          : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        color: isUser ? 'white' : 'text.primary',
                        boxShadow: isUser 
                          ? '0 3px 12px rgba(20,83,45,0.3)'
                          : '0 2px 8px rgba(0,0,0,0.1)',
                        border: isUser ? 'none' : '1px solid rgba(0,0,0,0.08)',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: isUser 
                            ? '0 4px 16px rgba(20,83,45,0.4)'
                            : '0 3px 12px rgba(0,0,0,0.15)',
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.3, fontSize: '0.85rem' }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.3, fontSize: '0.8rem' }}>
                        {item.message}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block', 
                          mt: 0.5, 
                          opacity: 0.7,
                          fontSize: '0.65rem'
                        }}
                      >
                        {item.timestamp}
                      </Typography>
                      
                      {/* Sent/Cancel buttons for pending forwarded messages */}
                      {isPendingForward && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          mt: 1,
                          justifyContent: 'flex-end'
                        }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleForwardSent}
                            sx={{
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              fontSize: '0.7rem',
                              px: 1.5,
                              py: 0.3,
                              borderRadius: '12px',
                              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                                transform: 'translateY(-1px)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            Sent
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleForwardCancel}
                            sx={{
                              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              color: 'white',
                              fontSize: '0.7rem',
                              px: 1.5,
                              py: 0.3,
                              borderRadius: '12px',
                              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                                transform: 'translateY(-1px)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                );
              })}
            </Box>
            
            {/* Scroll to Bottom Button for User Chat */}
            {showUserChatScrollToBottom && (
              <Fab
                size="small"
                onClick={scrollUserChatToBottom}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  background: 'linear-gradient(135deg, #166534 0%, #14532d 100%)',
                  color: 'white',
                  boxShadow: '0 4px 20px rgba(20,83,45,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #14532d 0%, #059669 100%)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 25px rgba(20,83,45,0.4)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 10,
                }}
              >
                <KeyboardArrowDownIcon />
              </Fab>
            )}
            
            {/* Unread Count Badge for User Chat */}
            {userChatUnreadCount > 0 && (
              <Fab
                size="small"
                onClick={scrollUserChatToBottom}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  boxShadow: '0 4px 20px rgba(239, 68, 68, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 25px rgba(239, 68, 68, 0.6)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 10,
                }}
              >
                <Badge badgeContent={userChatUnreadCount} color="error">
                  <KeyboardArrowDownIcon />
                </Badge>
              </Fab>
            )}

          </Paper>
        </Grid>
      </Grid>
      </Box>



      {/* Context Menus and Dialogs */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleAddComment}>Add/View Comment</MenuItem>
      </Menu>

      <Menu
        anchorEl={msgMenuAnchorEl}
        open={Boolean(msgMenuAnchorEl)}
        onClose={handleMsgMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleAssignUser}>Assign User</MenuItem>
        <MenuItem onClick={handleForwardMessage}>Forward Message</MenuItem>
      </Menu>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)}>
        <DialogTitle>Assign User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            type="text"
            fullWidth
            value={assignUserValue}
            onChange={e => setAssignUserValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssignUserSave} variant="contained">Assign</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)}>
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            type="text"
            fullWidth
            multiline
            minRows={2}
            value={commentValue}
            onChange={e => setCommentValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCommentSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={forwardDialogOpen} onClose={() => setForwardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Forward Message</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Message Content"
            type="text"
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            value={forwardMessageValue}
            onChange={e => setForwardMessageValue(e.target.value)}
            InputProps={{
              readOnly: false,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForwardDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleForwardMessageSave} variant="contained" color="primary">Forward</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} elevation={6} variant="filled" sx={{ fontSize: 16, fontWeight: 600, borderRadius: 2 }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default Interactions;